import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { errorCode } from '@infrastructure/admin-security-api';
import { AdminMailApi, adminMailSwitchedOff, newRequestKey, sendOutcomeUnknown } from '@infrastructure/admin-mail-api';
import { adminMailMessage } from './admin-mail-rules';
import { MailPreview } from './MailPreview';
const changedCodes = ['TEMPLATE_CHANGED', 'CONTENT_CHANGED', 'RECIPIENT_CHANGED', 'REQUEST_KEY_REUSED'];
export const ResendDialog = ({ target, onClose, onSent, onSwitchedOff }) => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');
    const retryKey = useRef(null);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);
    // A parent may pass a new callback on every render; the preview must still load only once per message.
    const switchedOff = useRef(onSwitchedOff);
    switchedOff.current = onSwitchedOff;
    const load = useCallback(async () => {
        setLoading(true);
        setPreview(null);
        try {
            const next = await AdminMailApi.resendPreview(target.id);
            if (!alive.current)
                return;
            if (next)
                setPreview(next);
            else
                setError('Het voorbeeld is onvolledig, dus deze mail kan niet worden verstuurd. Probeer het later opnieuw.');
        }
        catch (e) {
            if (!alive.current)
                return;
            if (adminMailSwitchedOff(e))
                switchedOff.current();
            else
                setError(adminMailMessage(e));
        }
        finally {
            if (alive.current)
                setLoading(false);
        }
    }, [target.id]);
    useEffect(() => { void load(); }, [load]);
    const send = async () => {
        // Only the complete preview on screen can be sent.
        if (busy || loading || !preview)
            return;
        const key = retryKey.current?.preview === preview ? retryKey.current.key : newRequestKey();
        retryKey.current = null;
        setBusy(true);
        setError('');
        setWarning('');
        try {
            const sent = await AdminMailApi.resend(target.id, preview, key);
            if (alive.current)
                onSent(sent, preview);
        }
        catch (e) {
            if (!alive.current)
                return;
            if (adminMailSwitchedOff(e)) {
                switchedOff.current();
                return;
            }
            if (changedCodes.includes(errorCode(e))) {
                setWarning(adminMailMessage(e));
                await load();
            }
            else {
                if (sendOutcomeUnknown(e))
                    retryKey.current = { preview, key };
                setError(adminMailMessage(e, undefined, true));
            }
        }
        finally {
            if (alive.current)
                setBusy(false);
        }
    };
    return (_jsxs(Dialog, { open: true, onClose: () => !busy && onClose(), fullWidth: true, maxWidth: 'md', "aria-labelledby": 'resend-mail-title', children: [_jsx(DialogTitle, { id: 'resend-mail-title', children: "Mail opnieuw versturen" }), _jsxs(DialogContent, { children: [_jsxs(Typography, { variant: 'body2', color: 'text.secondary', sx: { overflowWrap: 'anywhere' }, children: [preview ? _jsxs(_Fragment, { children: ["Opnieuw versturen naar ", _jsx("bdi", { children: preview.recipientName })] }) : 'Opnieuw versturen', " met de huidige gepubliceerde versie van", ' ', "\u201C", _jsx("bdi", { children: preview?.definitionName || target.definitionName }), "\u201D. Het originele bericht blijft ongewijzigd."] }), warning && _jsx(Alert, { severity: 'warning', sx: { mt: 2 }, children: warning }), error && _jsx(Alert, { severity: 'error', sx: { mt: 2 }, children: error }), loading ? (_jsx(Box, { py: 4, textAlign: 'center', children: _jsx(CircularProgress, { size: 24, "aria-label": 'Voorbeeld laden' }) })) : preview && (_jsx(Box, { mt: 2, children: _jsx(MailPreview, { preview: preview, originalLabel: `${target.stateLabel} · ${new Date(preview.originalCreatedUtc || target.createdUtc).toLocaleString()}` }) }))] }), _jsxs(DialogActions, { children: [_jsx(Button, { disabled: busy, onClick: onClose, children: "Annuleren" }), _jsx(Button, { "data-action-motion": 'true', variant: 'contained', disabled: busy || loading || !preview, onClick: send, children: busy ? 'Bezig…' : 'Versturen' })] })] }));
};
